import { isScrollLocked } from "core/scroll-lock";
import {
  animateSlideDrawer,
  initSlidePanel,
} from "motion/drawer-slide";
import { SlideDrawerElement } from "system/slide-drawer";
import { closeAllMegamenuCategorias, initMegamenuCategorias } from "system/megamenu-categorias";

const NAV_DELAY = 120;

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector(".section-header");
    this.alwaysSticky = this.dataset.stickyType === "always";
    this.bounds = {};
    this.scrollTop = 0;
    this.preventReveal = false;

    this.setHeight();

    if (this.classList.contains("header-wrapper--transparent")) {
      document.body.classList.add("header-is-transparent");
    }

    this._mq = window.matchMedia("(max-width: 990px)");
    this._onResize = () => this.setHeight();
    this._mq.addEventListener("change", this._onResize);

    if (this.alwaysSticky && this.header) {
      this.header.classList.add("shopify-section-header-sticky");
    }

    // rAF-gate: coalesce a burst of scroll events into one class-write per frame.
    this._scrollRaf = 0;
    this._onScroll = () => {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = 0;
        this.handleScroll();
      });
    };
    window.addEventListener("scroll", this._onScroll, { passive: true });

    // handleScroll() already no-ops while scroll-locked, but that only freezes
    // whatever hidden/visible state the header was already in — if the user
    // had scrolled down (header hidden) right before opening an overlay that
    // locks scroll (search, drawer, cart), the header stayed hidden the whole
    // time, leaving a visible gap above panels anchored to --header-offset
    // (e.g. the search panel). Force a reveal the instant the lock engages.
    this._onScrollLock = () => {
      this.classList.add("scroll-lock-active");
      this.header?.classList.remove("shopify-section-header-hidden");
    };
    this._onScrollUnlock = () => this.classList.remove("scroll-lock-active");
    document.addEventListener("rumah:scroll-lock", this._onScrollLock);
    document.addEventListener("rumah:scroll-unlock", this._onScrollUnlock);

    if (this.header) {
      const observer = new IntersectionObserver((entries) => {
        this.bounds = entries[0].intersectionRect;
        observer.disconnect();
      });
      observer.observe(this.header);
    }
  }

  disconnectedCallback() {
    document.body.classList.remove("header-is-transparent");
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    window.removeEventListener("scroll", this._onScroll);
    this._mq.removeEventListener("change", this._onResize);
    document.removeEventListener("rumah:scroll-lock", this._onScrollLock);
    document.removeEventListener("rumah:scroll-unlock", this._onScrollUnlock);
  }

  setHeight() {
    const topBar = this.querySelector(".header__top-bar");
    const mainBar = this.querySelector(".header");
    const topBarHeight = topBar?.offsetHeight ?? 0;
    const barHeight = mainBar?.offsetHeight ?? 0;
    const total = this.header?.offsetHeight ?? topBarHeight + barHeight;

    document.documentElement.style.setProperty("--header-top-bar-height", `${topBarHeight}px`);
    document.documentElement.style.setProperty("--header-bar-height", `${barHeight}px`);
    document.documentElement.style.setProperty("--header-height", `${total}px`);
    document.documentElement.style.setProperty("--header-offset", `${total}px`);
  }

  handleScroll() {
    if (!this.header) return;
    if (
      isScrollLocked() ||
      this.classList.contains("scroll-lock-active") ||
      this.classList.contains("header-menu-open")
    ) {
      return;
    }

    const y = window.scrollY;

    if (this.classList.contains("header-wrapper--transparent")) {
      this.classList.toggle("is-scrolled", y > 20);
    }

    if (y > this.scrollTop && y > this.bounds.bottom) {
      this.header.classList.add("scrolled-past-header");
      if (this.alwaysSticky) return;
      this.header.classList.add("shopify-section-header-hidden", "shopify-section-header-sticky");
    } else if (y < this.scrollTop && y > this.bounds.bottom) {
      this.header.classList.add("scrolled-past-header");
      if (this.alwaysSticky) return;
      if (!this.preventReveal) {
        this.header.classList.add("shopify-section-header-sticky");
        this.header.classList.remove("shopify-section-header-hidden");
      }
    } else if (y <= this.bounds.top) {
      this.header.classList.remove("scrolled-past-header", "shopify-section-header-hidden", "shopify-section-header-sticky");
    }

    this.scrollTop = y;
  }
}

class HeaderDrawer extends SlideDrawerElement {
  static slideDirection = "left";

  /** @type {number} */
  #subEpoch = 0;

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #closeBtn = null;
  /** @type {HTMLElement | null} */
  #backBtn = null;
  /** @type {HTMLElement | null} */
  #titleEl = null;

  connectedCallback() {
    if (!this.dataset.portaled) {
      // Marcar ANTES de mover: appendChild de un nodo conectado re-dispara
      // connectedCallback en sincrónico — sin el flag puesto recursa infinito.
      // La re-entrada (ya marcada) cae al init de abajo una sola vez.
      this.dataset.portaled = "true";
      document.body.appendChild(this);
      return;
    }

    this.querySelectorAll(".header__drawer-subpanel").forEach((panel) => {
      if (panel instanceof HTMLElement) initSlidePanel(panel, "right");
    });

    this.#closeBtn = this.querySelector(".header-drawer__close");
    this.#backBtn = this.querySelector(".header-drawer__back");
    this.#titleEl = this.querySelector("[data-drawer-header-title]");

    this.resolveTrigger();
    super.connectedCallback();
  }

  /** @param {AbortSignal} signal */
  onConnected(signal) {
    this.querySelectorAll("details.header__drawer-sub > summary").forEach((summary) => {
      summary.addEventListener("click", (event) => this.onSubSummaryClick(event, summary), { signal });
    });

    // «Volver» de la barra fija: cierra el subpanel abierto más profundo.
    this.#backBtn?.addEventListener(
      "click",
      () => {
        const open = [...this.querySelectorAll("details.header__drawer-sub[open]")];
        const deepest = open[open.length - 1];
        if (deepest instanceof HTMLDetailsElement) void this.closeSubmenu(deepest);
      },
      { signal },
    );
  }

  /**
   * Sincroniza la barra superior fija con la profundidad actual: «Cerrar» en la
   * raíz, «‹ Volver | Título» dentro de una categoría. El contenido se intercambia
   * en el sitio (misma altura) — sin ocultar la barra, así no hay salto ni FOUC.
   * @param {Element | null} [closing] Details que se está cerrando (se excluye de la cadena abierta).
   */
  #syncHeader(closing = null) {
    if (!this.#closeBtn || !this.#backBtn) return;

    const open = [...this.querySelectorAll("details.header__drawer-sub[open]")].filter(
      (details) => details !== closing,
    );
    const deepest = open[open.length - 1];

    if (deepest) {
      const title = deepest.querySelector(".header__drawer-back-title")?.textContent?.trim() ?? "";
      if (this.#titleEl) this.#titleEl.textContent = title;
      this.#closeBtn.setAttribute("hidden", "");
      this.#backBtn.removeAttribute("hidden");
    } else {
      this.#closeBtn.removeAttribute("hidden");
      this.#backBtn.setAttribute("hidden", "");
    }
  }

  /** @param {KeyboardEvent} event */
  onEscapeKey(event) {
    const openSub = this.querySelector("details.header__drawer-sub[open]");
    if (openSub instanceof HTMLDetailsElement) {
      void this.closeSubmenu(openSub);
      return true;
    }
    return false;
  }

  /** @param {AbortSignal} signal */
  bindDrawerTriggers(signal) {
    const onTrigger = (event) => {
      event.preventDefault();
      this.toggle();
    };

    this.trigger?.addEventListener("click", onTrigger, { signal });
  }

  resolveTrigger() {
    const id = this.id;
    this.#trigger =
      (id ? document.querySelector(`[aria-controls="${id}"]`) : null) ??
      document.querySelector("[data-drawer-open]");
  }

  get trigger() {
    return this.#trigger;
  }

  onDrawerOpen() {
    closeAllMegamenuCategorias();
    document.querySelector("sticky-header")?.classList.add("header-menu-open");
  }

  onDrawerClose() {
    document.querySelector("sticky-header")?.classList.remove("header-menu-open");
    this.#subEpoch += 1;
    this.resetNested();
    this.trigger?.focus({ preventScroll: true });
  }

  /**
   * @param {Event} event
   * @param {HTMLElement} summary
   */
  onSubSummaryClick(event, summary) {
    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement)) return;
    event.preventDefault();

    if (details.hasAttribute("open")) {
      void this.closeSubmenu(details);
    } else {
      void this.openSubmenu(details, summary);
    }
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  closeSubmenuInstant(details) {
    const panel = details.querySelector(".header__drawer-subpanel");
    if (panel instanceof HTMLElement) {
      initSlidePanel(panel, "right");
      unanchorSubpanel(panel);
    }
    details.removeAttribute("open");
    details.querySelector("summary")?.setAttribute("aria-expanded", "false");
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} summary
   */
  async openSubmenu(details, summary) {
    const panel = details.querySelector(".header__drawer-subpanel");
    if (!(panel instanceof HTMLElement)) return;

    const scrollHost = details.parentElement?.closest(
      ".header-drawer__body, .header__drawer-subpanel",
    );

    this.#subEpoch += 1;
    const epoch = this.#subEpoch;

    // Solo cierra hermanos, nunca ancestros: L1/L2/L3 comparten la misma
    // clase, así que sin el chequeo .contains() abrir L3 encontraba a su
    // propio padre L2 (también "open") y lo cerraba, colapsando la vista de
    // vuelta a L1 — la transición se sentía rota más allá del primer nivel.
    this.querySelectorAll("details.header__drawer-sub[open]").forEach((other) => {
      if (other !== details && !other.contains(details)) this.closeSubmenuInstant(other);
    });

    details.setAttribute("open", "");
    summary.setAttribute("aria-expanded", "true");
    // Con [open] el padre deja de desplazarse (header.drawer.css); recién
    // entonces su scrollTop es estable para anclar el subpanel.
    if (scrollHost instanceof HTMLElement) anchorSubpanel(panel, scrollHost);

    // Actualiza la barra fija en el mismo frame en que empieza a deslizar el
    // subpanel: el título aparece ya puesto, sin flash de la fila anterior.
    this.#syncHeader();

    try {
      await animateSlideDrawer({
        panel,
        root: details,
        open: true,
        direction: "right",
        presetId: this.presetId,
      });
    } catch {
      /* interrupted */
    }

    if (epoch !== this.#subEpoch) return;
  }

  /** @param {HTMLDetailsElement} details */
  async closeSubmenu(details) {
    const panel = details.querySelector(".header__drawer-subpanel");
    const summary = details.querySelector("summary");

    this.#subEpoch += 1;
    const epoch = this.#subEpoch;

    // La barra refleja de inmediato el nivel padre (excluyendo el que se cierra)
    // mientras el subpanel aún se desliza hacia afuera.
    this.#syncHeader(details);

    if (panel instanceof HTMLElement) {
      try {
        await animateSlideDrawer({
          panel,
          root: details,
          open: false,
          direction: "right",
          presetId: this.presetId,
        });
      } catch {
        /* interrupted */
      }
    }

    if (epoch !== this.#subEpoch) return;

    details.removeAttribute("open");
    summary?.setAttribute("aria-expanded", "false");
    if (panel instanceof HTMLElement) unanchorSubpanel(panel);
  }

  resetNested() {
    this.querySelectorAll("details.header__drawer-sub").forEach((details) => {
      const panel = details.querySelector(".header__drawer-subpanel");
      if (panel instanceof HTMLElement) {
        initSlidePanel(panel, "right");
        unanchorSubpanel(panel);
      }
      details.removeAttribute("open");
      details.querySelector("summary")?.setAttribute("aria-expanded", "false");
    });
    this.#syncHeader();
  }
}

/**
 * El subpanel se posiciona (inset:0) dentro del scroll container de su nivel
 * padre (body para L1, subpanel padre para L2/L3), así que con el padre
 * desplazado quedaría corrido y dejaría ver la lista de debajo. Resetear el
 * scrollTop del padre no basta: en iOS se pierde si el scroll por inercia sigue
 * en curso. Se ancla al área visible del padre, y al volver la lista sigue
 * donde el usuario la dejó.
 * @param {HTMLElement} panel
 * @param {HTMLElement} scrollHost
 */
function anchorSubpanel(panel, scrollHost) {
  const offset = scrollHost.scrollTop;
  panel.style.top = `${offset}px`;
  panel.style.bottom = `${-offset}px`;
}

/** @param {HTMLElement} panel */
function unanchorSubpanel(panel) {
  panel.style.removeProperty("top");
  panel.style.removeProperty("bottom");
}

/** @param {HTMLElement} root */
function initNavDropdowns(root) {
  root.querySelectorAll(".header__menu-item--has-dropdown").forEach((item) => {
    if (item.classList.contains("header__menu-item--categorias-fullscreen")) return;

    let timer = 0;

    item.addEventListener("mouseenter", () => {
      clearTimeout(timer);
      closeAllMegamenuCategorias();
      root.querySelectorAll(".header__menu-item--has-dropdown.is-open").forEach((other) => {
        if (other !== item) other.classList.remove("is-open");
      });
      item.classList.add("is-open");
    });

    item.addEventListener("mouseleave", () => {
      timer = window.setTimeout(() => item.classList.remove("is-open"), NAV_DELAY);
    });
  });

  document.addEventListener("click", (event) => {
    if (event.target instanceof Element && event.target.closest(".header__menu-item--categorias-fullscreen")) {
      return;
    }

    closeAllMegamenuCategorias();

    if (event.target instanceof Element && event.target.closest(".header__menu-item--has-dropdown")) {
      return;
    }

    root.querySelectorAll(".header__menu-item--has-dropdown.is-open").forEach((item) => {
      item.classList.remove("is-open");
    });
  });
}

function initDrawerHelpers() {
  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;

    const backBtn = event.target.closest("[data-drawer-back]");
    if (!backBtn) return;

    const details = backBtn.closest("details.header__drawer-sub");
    const host = document.querySelector("header-drawer");
    if (details instanceof HTMLDetailsElement) {
      host?.closeSubmenu?.(details);
    }
  });
}

function registerHeaderElements() {
  if (!customElements.get("sticky-header")) {
    customElements.define("sticky-header", StickyHeader);
  }
  if (!customElements.get("header-drawer")) {
    customElements.define("header-drawer", HeaderDrawer);
  }
}

/** @param {HTMLElement} [root] */
export function initHeader(root = document.body) {
  registerHeaderElements();
  initNavDropdowns(root);
  initDrawerHelpers();
  initMegamenuCategorias(root);
}

registerHeaderElements();
